import { createSystem, defaultConfig, defineConfig } from '@chakra-ui/react';
import { buttonRecipe } from './components/button.recipe';
import { cardButtonRecipe } from './components/cardButton.recipe';
import { inputRecipe } from './components/input.recipe';
import { searchInputRecipe } from './components/searchInput.recipe';
import { radioGroupSlotRecipe } from './components/radio.recipe';
import { fileUploadSlotRecipe } from './components/fileUpload.recipe';
import { selectSlotRecipe } from './components/select.recipe';
import { BrandKey } from './brand';

/**
 * Build the Chakra theme for a brand-key (ADR 115 Phase 5).
 *
 * `makeTheme` takes the {@link BrandKey} enum directly. Only `safe-plus`
 * carries a distinct accent palette (the `safe.*` blue); `aegix` and `dirs`
 * share the default teal accent.
 */
export function makeTheme(brandKey: BrandKey) {
  const isSafePlus = brandKey === BrandKey.SafePlus;
  const config = defineConfig({
    // Chrome <99 does not support @layer (kiosk WebView 93) — disable layer wrapping
    disableLayers: true,
    globalCss: {
      body: {
        bg: 'surface.primary',
        color: 'text.primary',
      },
      '*:focus-visible': {
        outline: '2px solid',
        outlineColor: 'border.action',
        outlineOffset: '2px',
      },
      '@media (prefers-reduced-motion: reduce)': {
        // Must be @ts-ignore, never an expect-error directive: the TS error
        // only fires under some consumer build configs (chakra strict types),
        // and an unused expect-error breaks the SMS/ONE/Beacon/AIM web builds.
        // The ban-ts-comment lint rule is disabled just below for that reason.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore nested selector not in Chakra SystemStyleObject type but valid CSS-in-JS
        '*, *::before, *::after': {
          animationDuration: '0.01ms !important',
          animationIterationCount: '1 !important',
          transitionDuration: '0.01ms !important',
          scrollBehavior: 'auto !important',
        },
      } as any,
      '.sr-only': {
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        borderWidth: '0',
      },
    },
    theme: {
      tokens: {
        colors: {
          // Color palettes for components
          red: {
            50: { value: '#FFEAE6' },
            100: { value: '#FFC4B8' },
            200: { value: '#FF9A8A' },
            300: { value: '#FF6C57' },
            400: { value: '#FA3F29' },
            500: { value: '#E70E00' },
            600: { value: '#B90B00' },
            700: { value: '#8B0800' },
            800: { value: '#5C0500' },
            900: { value: '#2E0300' },
            950: { value: '#170100' },
          },
          blue: {
            50: { value: '#E6F3FF' },
            100: { value: '#B8DBFF' },
            200: { value: '#8AC4FF' },
            300: { value: '#5CADFF' },
            400: { value: '#2E96FF' },
            500: { value: '#0A84FF' },
            600: { value: '#006AD4' },
            700: { value: '#0050A3' },
            800: { value: '#003671' },
            900: { value: '#001C3E' },
            950: { value: '#000E1F' },
          },
          teal: {
            50: { value: '#E6F5F5' },
            100: { value: '#94DAD9' },
            200: { value: '#6BC4C3' },
            300: { value: '#35BEBD' },
            400: { value: '#00C2BF' },
            500: { value: '#00B6B4' },
            600: { value: '#00A09D' },
            700: { value: '#008D82' },
            800: { value: '#00585F' },
            900: { value: '#00373D' },
            950: { value: '#1C1C1E' },
            1000: { value: '#00B6B414' },
          },
          safe: {
            500: { value: '#1589C4' },
            600: { value: '#1275A7' },
            700: { value: '#0F618A' },
          },
          darkOrange: {
            50: { value: '#FFEFE6' },
            100: { value: '#FFD1BF' },
            200: { value: '#FFB199' },
            300: { value: '#FF8C6E' },
            400: { value: '#FF6A47' },
            500: { value: '#FF4F19' },
            600: { value: '#D43A0C' },
            700: { value: '#A62D08' },
            800: { value: '#731F05' },
            900: { value: '#401003' },
            950: { value: '#200801' },
          },
          orange: {
            50: { value: '#FFF8E6' },
            100: { value: '#FFEBB9' },
            200: { value: '#FFDE8C' },
            300: { value: '#FFCF59' },
            400: { value: '#FFC02D' },
            500: { value: '#FEA009' },
            600: { value: '#D48400' },
            700: { value: '#A26500' },
            800: { value: '#704600' },
            900: { value: '#3C2600' },
            950: { value: '#1E1300' },
          },
          yellow: {
            50: { value: '#FFFBE5' },
            100: { value: '#FFF3B3' },
            200: { value: '#FFEB80' },
            300: { value: '#FFE24D' },
            400: { value: '#FFDA1A' },
            500: { value: '#FECE00' },
            600: { value: '#D6AC00' },
            700: { value: '#A38300' },
            800: { value: '#715B00' },
            900: { value: '#3E3200' },
            950: { value: '#1F1900' },
            1000: { value: '#F5A623' },
          },
          green: {
            50: { value: '#EBFBEE' },
            100: { value: '#C8F5D0' },
            200: { value: '#A3EFB1' },
            300: { value: '#7CE890' },
            400: { value: '#55E070' },
            500: { value: '#32D74B' },
            600: { value: '#25B53D' },
            700: { value: '#1A8A2D' },
            800: { value: '#10601E' },
            900: { value: '#08350F' },
            950: { value: '#041B07' },
          },
          lightBlue: {
            50: { value: '#F0FAFF' },
            100: { value: '#D3F2FF' },
            200: { value: '#B3E8FF' },
            300: { value: '#90DEFF' },
            400: { value: '#75D7FF' },
            500: { value: '#64D2FF' },
            600: { value: '#49B0DB' },
            700: { value: '#3386AE' },
            800: { value: '#205D7D' },
            900: { value: '#103346' },
            950: { value: '#081A23' },
          },
          darkBlue: {
            50: { value: '#F0F0FD' },
            100: { value: '#D3D3FA' },
            200: { value: '#B6B6F6' },
            300: { value: '#9A9AF1' },
            400: { value: '#7D7DEC' },
            500: { value: '#5E5CE6' },
            600: { value: '#4A48C3' },
            700: { value: '#373598' },
            800: { value: '#25236C' },
            900: { value: '#14123E' },
            950: { value: '#0A091F' },
          },
          purple: {
            50: { value: '#F7EBF9' },
            100: { value: '#EAC9EF' },
            200: { value: '#DDAAE4' },
            300: { value: '#CE8AD8' },
            400: { value: '#BF6BCD' },
            500: { value: '#9737A8' },
            600: { value: '#8A2EA0' },
            700: { value: '#702583' },
            800: { value: '#551C65' },
            900: { value: '#3B1247' },
            950: { value: '#200A27' },
          },
          darkRed: {
            50: { value: '#FFE5EB' },
            100: { value: '#FFB3C6' },
            200: { value: '#FF80A0' },
            300: { value: '#FF4D7A' },
            400: { value: '#FA1A58' },
            500: { value: '#EE003E' },
            600: { value: '#C30032' },
            700: { value: '#970027' },
            800: { value: '#6C001B' },
            900: { value: '#400010' },
            950: { value: '#200008' },
          },
          neutral: {
            50: { value: '#F8F8FA' },
            100: { value: '#F2F2F7' },
            200: { value: '#E5E5EA' },
            300: { value: '#D1D1D6' },
            400: { value: '#C7C7CC' },
            500: { value: '#AEAEB2' },
            600: { value: '#8E8E93' },
            700: { value: '#636366' },
            800: { value: '#48484A' },
            900: { value: '#3A3A3C' },
            950: { value: '#222224' },
            1000: { value: '#C7C7CC26' },
          },
          safety: {
            medical: {
              light: { value: '#1F97C4' },
              base: { value: '#1F97C4' }, // #209ecd
              dark: { value: '#1F97C4' },
            },
            unsafe: {
              light: { value: '#ec624e' },
              base: { value: '#E24F39' }, // #d6351c
              dark: { value: '#9f2714' },
            },
            safe: {
              light: { value: '#60ed60' },
              base: { value: '#2ED157' },
              dark: { value: '#2aa12a' },
            },
            cleared: {
              light: { value: '#8ea7ea' },
              base: { value: '#667FC0' }, // #708bd1
              dark: { value: '#5a70aa' },
            },
            reset: {
              light: { value: '#cccccc' },
              base: { value: '#9c9c9c' },
              dark: { value: '#7c7b7b' },
            },
            unknown: {
              light: { value: '#e6e626' },
              base: { value: '#cccc0e' },
              dark: { value: '#979715' },
            },
          },
          blackAlpha: {
            200: { value: '#00000033' },
          },
          whiteAlpha: {
            200: { value: '#ffffff33' },
          },
        },
        fonts: {
          body: { value: 'Inter, sans-serif' },
          heading: { value: 'Inter, sans-serif' },
        },
        lineHeights: {
          caption: { value: 1.333 },
          body: { value: 1.428 },
          subheading: { value: 1.818 },
          h2: { value: 1.5 },
          h1: { value: 1.2 },
        },
        sizes: {
          container: { value: '1200px' },
          controlHeightSm: { value: '32px' },
          controlHeight: { value: '38px' },
          controlHeightLg: { value: '44px' },
        },
        radii: {
          half: { value: '50%' }, // For perfectly round elements like avatars
        },
        shadows: {
          sm: { value: '0 1px 2px 0 #0000000D' },
          base: { value: '0 1px 3px 0 #0000001A, 0 1px 2px 0 #0000000F' }, // Often just 'md'
          md: { value: '0 4px 6px -1px #0000001A, 0 2px 4px -1px #0000000F' },
          lg: { value: '0 10px 15px -3px #0000001A, 0 4px 6px -2px #0000000D' },
          xl: {
            value: '0 20px 25px -5px #0000001A, 0 10px 10px -5px #0000000A',
          },
          '2xl': { value: '0 25px 50px -12px #00000040' },
          card: { value: '0px 2.5px 4px -1px rgba(0, 0, 0, 0.15)' },
          inner: { value: 'inset 0 2px 4px 0 #0000000F' },
          outline: {
            value: isSafePlus
              ? '0 0 0 3px {colors.safe.500/40}'
              : '0 0 0 3px {colors.teal.500/40}',
          },
        },
        borderWidths: {
          1: { value: '1px' },
          2: { value: '2px' },
          3: { value: '3px' },
          4: { value: '4px' },
        },
      },
      semanticTokens: {
        colors: {
          text: {
            primary: {
              value: { base: '{colors.teal.950}', _dark: 'white' },
            },
            secondary: {
              value: { base: '{colors.gray.800}', _dark: '{colors.gray.300}' },
            },
            tertiary: {
              value: { base: '{colors.gray.600}', _dark: '{colors.gray.400}' },
            },
            danger: {
              value: {
                base: '{colors.red.500}',
                _dark: '{colors.darkRed.500}',
              },
            },
            drill: {
              value: { base: '{colors.blue.500}', _dark: '{colors.blue.500}' },
            },
            reunification: {
              value: {
                base: '{colors.purple.500}',
                _dark: '{colors.purple.500}',
              },
            },
            report: {
              value: {
                base: '{colors.yellow.600}',
                _dark: '{colors.yellow.600}',
              },
            },
            action: {
              value: {
                base: isSafePlus ? '{colors.safe.500}' : '{colors.teal.500}',
                _dark: isSafePlus ? '{colors.safe.500}' : '{colors.teal.500}',
              },
            },
            hover: {
              value: { base: '{colors.teal.900}', _dark: '{colors.gray.50}' },
            },
            default: {
              value: { base: '{colors.teal.950}', _dark: 'colors.teal.950' },
            },
          },
          surface: {
            primary: {
              value: { base: 'white', _dark: '{colors.teal.950}' },
            },
            secondary: {
              value: {
                base: '{colors.neutral.100}',
                _dark: '{colors.neutral.900}',
              },
            },
            tertiary: {
              value: { base: '{colors.gray.200}', _dark: '{colors.gray.700}' },
            },
            danger: {
              value: {
                base: '{colors.red.500}',
                _dark: '{colors.darkRed.500}',
              },
            },
            drill: {
              value: { base: '{colors.blue.500}', _dark: '{colors.blue.500}' },
            },
            reunification: {
              value: {
                base: '{colors.purple.500}',
                _dark: '{colors.purple.500}',
              },
            },
            report: {
              value: {
                base: '{colors.yellow.600}',
                _dark: '{colors.yellow.600}',
              },
            },
            action: {
              value: {
                base: isSafePlus ? '{colors.safe.500}' : '{colors.teal.500}',
                _dark: isSafePlus ? '{colors.safe.500}' : '{colors.teal.500}',
              },
            },
            select: {
              value: {
                base: isSafePlus
                  ? '{colors.safe.500}/40'
                  : '{colors.teal.500}/40',
                _dark: isSafePlus
                  ? '{colors.safe.500}/30'
                  : '{colors.teal.500}/30',
              },
            },
            input: {
              value: { base: '{colors.gray.100}', _dark: '{colors.gray.950}' },
            },
            choice: {
              value: { base: 'white', _dark: 'white' },
            },
            dim: {
              value: { base: '{colors.gray.400}', _dark: '{colors.gray.900}' },
            },
            hover: {
              value: { base: 'white/80', _dark: '{colors.gray.950}/80' },
            },
            menu: {
              value: { base: 'black/20', _dark: 'black/20' },
            },
            menuSelect: {
              value: {
                base: isSafePlus ? 'colors.safe.500/8' : 'colors.teal.500/8',
                _dark: 'colors.nuetral.400/15',
              },
            },
            unknown: {
              value: { base: 'white/10', _dark: '10' },
            },
            card: {
              value: {
                base: '{colors.neutral.100}',
                _dark: '{colors.neutral.900}',
              },
            },
            searchInput: {
              value: {
                base: '{colors.neutral.100}',
                _dark: '{colors.neutral.950}',
              },
            },
            chatCardActive: {
              value: {
                base: '{colors.teal.1000}',
                _dark: 'colors.neutral.1000',
              },
            },
            sendChat: {
              value: {
                base: '{colors.teal.100}',
                _dark: 'colors.teal.100',
              },
            },
            tableHeader: {
              value: {
                base: '{colors.neutral.200}',
                _dark: '{colors.neutral.800}',
              },
            },
            menuActive: {
              value: {
                base: '{colors.teal.1000}',
                _dark: '{colors.neutral.1000}',
              },
            },
            overlay: {
              value: {
                base: 'rgba(0, 0, 0, 0.4)',
                _dark: 'rgba(0, 0, 0, 0.6)',
              },
            },
            evacuated: {
              value: {
                base: '{colors.yellow.100}',
                _dark: '{colors.yellow.100}',
              },
            },
            chatCallBg: {
              value: {
                base: '{colors.blackAlpha.200}',
                _dark: '{colors.whiteAlpha.200}',
              },
            },
          },
          button: {
            primary: {
              value: {
                base: isSafePlus ? '{colors.safe.500}' : '{colors.teal.500}',
                _dark: isSafePlus ? '{colors.safe.500}' : '{colors.teal.500}',
              },
            },
            primaryHover: {
              value: {
                base: isSafePlus ? '{colors.safe.600}' : '{colors.teal.600}',
                _dark: isSafePlus ? '{colors.safe.600}' : '{colors.teal.600}',
              },
            },
            primaryActive: {
              value: {
                base: isSafePlus ? '{colors.safe.700}' : '{colors.teal.700}',
                _dark: isSafePlus ? '{colors.safe.700}' : '{colors.teal.700}',
              },
            },
            secondary: {
              value: { base: '{colors.gray.200}', _dark: '{colors.gray.700}' },
            },
            danger: {
              value: {
                base: '{colors.red.500}',
                _dark: '{colors.darkRed.500}',
              },
            },
            drill: {
              value: { base: '{colors.blue.500}', _dark: '{colors.blue.500}' },
            },
            reunification: {
              value: {
                base: '{colors.purple.500}',
                _dark: '{colors.purple.500}',
              },
            },
            report: {
              value: {
                base: '{colors.yellow.600}',
                _dark: '{colors.yellow.600}',
              },
            },
            success: {
              value: {
                base: '{colors.green.500}',
                _dark: '{colors.green.500}',
              },
            },
            unsafe: {
              value: {
                base: '{colors.safety.unsafe.base}',
                _dark: '{colors.safety.unsafe.base}',
              },
            },
            safe: {
              value: {
                base: '{colors.safety.safe.base}',
                _dark: '{colors.safety.safe.base}',
              },
            },
            clear: {
              value: {
                base: '{colors.safety.cleared.base}',
                _dark: '{colors.safety.cleared.base}',
              },
            },
            reset: {
              value: {
                base: '{colors.safety.reset.base}',
                _dark: '{colors.safety.reset.base}',
              },
            },
            icon: {
              value: {
                base: '{colors.gray.100}',
                _dark: '{colors.gray.700}',
              },
            },
          },
          border: {
            action: {
              value: {
                base: isSafePlus ? '{colors.safe.700}' : '{colors.teal.700}',
                _dark: isSafePlus ? '{colors.safe.700}' : '{colors.teal.700}',
              },
            },
            primary: {
              value: { base: '{colors.teal.100}', _dark: '{colors.teal.900}' },
            },
            secondary: {
              value: { base: '{colors.gray.200}', _dark: '{colors.gray.800}' },
            },
            tertiary: {
              value: { base: '{colors.gray.400}', _dark: '{colors.gray.600}' },
            },
            danger: {
              value: {
                base: '{colors.red.500}',
                _dark: '{colors.darkRed.500}',
              },
            },
            drill: {
              value: { base: '{colors.blue.500}', _dark: '{colors.blue.500}' },
            },
            reunification: {
              value: {
                base: '{colors.purple.500}',
                _dark: '{colors.purple.500}',
              },
            },
            report: {
              value: {
                base: '{colors.yellow.600}',
                _dark: '{colors.yellow.600}',
              },
            },
            success: {
              value: {
                base: '{colors.green.500}',
                _dark: '{colors.green.500}',
              },
            },
            unsafe: {
              value: {
                base: '{colors.safety.unsafe.base}',
                _dark: '{colors.safety.unsafe.base}',
              },
            },
            safe: {
              value: {
                base: '{colors.safety.safe.base}',
                _dark: '{colors.safety.safe.base}',
              },
            },
            clear: {
              value: {
                base: '{colors.safety.cleared.base}',
                _dark: '{colors.safety.cleared.base}',
              },
            },
            reset: {
              value: {
                base: '{colors.safety.reset.base}',
                _dark: '{colors.safety.reset.base}',
              },
            },
            card: {
              value: {
                base: '{colors.neutral.400}',
                _dark: '{colors.neutral.700}',
              },
            },
            line: {
              value: {
                base: '{colors.neutral.200}',
                _dark: '{colors.neutral.800}',
              },
            },
            sendChat: {
              value: {
                base: '{colors.teal.700}',
                _dark: 'colors.teal.700',
              },
            },
            evacuated: {
              value: {
                base: '{colors.yellow.600}',
                _dark: '{colors.yellow.600}',
              },
            },
          },
          shape: {
            stroke: {
              value: {
                base: '{colors.gray.700}/30',
                _dark: '{colors.gray.300}/30',
              },
            },
            fill: {
              value: {
                base: '{colors.gray.600}/15',
                _dark: '{colors.gray.400}/15',
              },
            },
            fillSelect: {
              value: {
                base: isSafePlus
                  ? '{colors.safe.500}/15'
                  : '{colors.teal.500}/15',
                _dark: isSafePlus
                  ? '{colors.safe.500}/15'
                  : '{colors.teal.500}/15',
              },
            },
          },
          defaultSafety: {
            default: {
              value: {
                base: '{colors.neutral.800}',
                _dark: '{colors.neutral.300}',
              },
            },
            primary: {
              value: {
                base: isSafePlus ? '{colors.safe.500}' : '{colors.teal.500}',
                _dark: isSafePlus ? '{colors.safe.500}' : '{colors.teal.500}',
              },
            },
          },
        },
        borders: {
          '0': {
            value: '0px none',
          },
          '1': {
            value: '1px solid',
          },
          '2': {
            value: '2px solid',
          },
          '3': {
            value: '3px solid',
          },
          '4': {
            value: '4px solid',
          },
        },
      },
      textStyles: {
        h1: {
          fontFamily: { value: '{fonts.heading}' },
          fontSize: { value: '{fontSizes.xl}' },
          fontWeight: { value: '{semibold}' },
          lineHeight: { value: '{lineHeights.h1}' },
        },
        h2: {
          fontFamily: { value: '{fonts.heading}' },
          fontSize: { value: '{fontSizes.lg}' },
          fontWeight: { value: '{medium}' },
          lineHeight: { value: '{lineHeights.h2}' },
        },
        subheading: {
          fontFamily: { value: '{fonts.body}' },
          fontSize: { value: '{fontSizes.xs}' },
          fontWeight: { value: '{semibold}' },
          lineHeight: { value: '{lineHeights.subheading}' },
          textTransform: 'capitalize',
        },
        body: {
          fontFamily: { value: '{fonts.body}' },
          fontSize: { value: '{fontSizes.md}' },
          fontWeight: { value: '{regular}' },
          lineHeight: { value: '{lineHeights.body}' },
        },
        bodyEmphasized: {
          fontFamily: { value: '{fonts.body}' },
          fontSize: { value: '{fontSizes.md}' },
          fontWeight: { value: '{semibold}' },
          lineHeight: { value: '{lineHeights.body}' },
        },
        caption: {
          fontFamily: { value: '{fonts.body}' },
          fontSize: { value: '{fontSizes.sm}' },
          fontWeight: { value: '{regular}' },
          lineHeight: { value: '{lineHeights.caption}' },
        },
        captionEmphasized: {
          fontFamily: { value: '{fonts.body}' },
          fontSize: { value: '{fontSizes.sm}' },
          fontWeight: { value: '{semibold}' },
          lineHeight: { value: '{lineHeights.caption}' },
        },
        button: {
          fontFamily: { value: '{fonts.heading}' },
          fontSize: { value: '{lg}' },
          fontWeight: { value: '{black}' },
          lineHeight: { value: '{lineHeights.subheading}' },
          letterSpacing: { value: 'widest' },
        },
      },
      recipes: {
        Button: buttonRecipe,
        CardButton: cardButtonRecipe,
        Input: inputRecipe,
        SearchInput: searchInputRecipe,
      },
      slotRecipes: {
        radioGroup: radioGroupSlotRecipe,
        fileUpload: fileUploadSlotRecipe,
        select: selectSlotRecipe,
        // Chrome 93 does not support dvw/dvh viewport units (kiosk WebView) — use vw/vh
        dialog: {
          className: 'chakra-dialog',
          slots: [
            'trigger',
            'backdrop',
            'positioner',
            'content',
            'title',
            'description',
            'closeTrigger',
            'header',
            'body',
            'footer',
          ],
          base: {
            backdrop: { w: '100vw', h: '100vh' },
            positioner: { width: '100vw', height: '100vh' },
          },
          variants: {
            size: {
              full: { content: { maxW: '100vw', minH: '100vh' } },
            },
          },
        },
        drawer: {
          className: 'chakra-drawer',
          slots: [
            'trigger',
            'backdrop',
            'positioner',
            'content',
            'title',
            'description',
            'closeTrigger',
            'header',
            'body',
            'footer',
          ],
          base: {
            backdrop: { h: '100vh' },
            positioner: { height: '100vh' },
            content: { maxH: '100vh' },
          },
          variants: {
            size: {
              full: { content: { h: '100vh' } },
            },
          },
        },
      },
    },
  });
  return createSystem(defaultConfig, config);
}
// export default createSystem(defaultConfig, config);
